import type React from "react";
import { useContext, useState } from "react";

import styled from "styled-components";

import { PayerTypes } from "api/models/CareEvent";
import { useDropInCallContext } from "contexts/DropInCallContext";
import { useFeatureToggleContext } from "contexts/FeatureToggleContext";
import { useProfileContext } from "contexts/ProfileContext";
import { Hidden } from "shared/atoms/Hidden";
import { VideoCallAwareContainer } from "shared/atoms/VideoCallAwareContainer/VideoCallAwareContainer";
import { CurrentPatientContext } from "utils/contexts";
import { Feature } from "utils/features/types";
import { userIsPartner, userIsPhysician, userIsTherapist } from "utils/profile/profileHelper";

import { PatientMenu } from "../PatientMenu";

import { PatientEndTreatmentForms } from "./PatientEndTreatmentForms";
import { PatientHeaderGuide } from "./PatientHeaderGuide";
import { PatientHeaderInfo } from "./PatientHeaderInfo";
import { isPatientDischarged } from "./PatientHeaderInfo/helpers";
import { PatientHeaderBanner } from "./PatientHeaderInfo/PatientHeaderBanner";
import { PhysicianToDoTable } from "./PhysicianToDoTable";
import { ScheduleCallBox } from "./ScheduleCallBox";
import { SignNotesTable } from "./SignNotesTable";
import { UpcomingAppointments } from "./UpcomingAppointments";
import { UserCourseNotificationBox } from "./UserCourseNotificationBox";

export const PatientHeader: React.FC = () => {
  const { patient, patientError } = useContext(CurrentPatientContext);
  const { hasFeature } = useFeatureToggleContext();
  const { profile } = useProfileContext();
  const isPhysician = userIsPhysician(profile);
  const isTherapist = userIsTherapist(profile);
  const [showEndTreatmentForm, setshowEndTreatmentForm] = useState(!!patient.end_treatment_at);
  const { currentSession } = useDropInCallContext();

  // Guides - only one guide is shown at a time
  const showGuide = () => {
    if (userIsPartner(profile)) {
      return null;
    }

    if (patient.pt_on_demand_enabled) {
      return <PatientHeaderGuide patient={patient} type="pt_on_demand_call" />;
    }
    if (!isPatientDischarged(patient) && patient.ready_for_discharge) {
      if (patient.premium_type === "none") {
        return <PatientHeaderGuide patient={patient} type="premium_none" />;
      }
      if (patient.premium_type === "selfcare") {
        return <PatientHeaderGuide patient={patient} type="premium_selfcare" />;
      }
      if (patient.premium_type === "guided_care") {
        return <PatientHeaderGuide patient={patient} type="premium_guided_care" />;
      }
    }
    return null;
  };

  const isDropInCallActive =
    currentSession &&
    patient.id === currentSession.patient_id &&
    (currentSession?.state === "claimed" || currentSession?.state === "in_review");

  const showScheduleCallBox =
    hasFeature(Feature.PATIENT_SCHEDULE_CALL_BOX) && patient?.appointment_to_schedule && !isDropInCallActive;

  const showUserCourseNotification = isTherapist && patient.awaiting_treatment_ready;

  // FE fix to hide low adherence notification for PTonDemand patients
  // https://jointacademy.atlassian.net/browse/CCC-1192
  const hideLowAdherenceNotification = patient?.pt_on_demand_enabled;

  return (
    <Content>
      <PatientHeaderInfo
        setShowEndTreatmentForm={setshowEndTreatmentForm}
        showEndTreatmentForm={showEndTreatmentForm}
      />
      <Hidden type="aboveTablet">
        <PatientHeaderBanner />
      </Hidden>
      {showEndTreatmentForm && <PatientEndTreatmentForms patient={patient} />}
      {showScheduleCallBox && !hideLowAdherenceNotification && (
        <ScheduleCallBox
          patientId={patient?.id}
          appointmentToSchedule={patient?.appointment_to_schedule}
          ceStructure={patient?.ce_structure}
          isPremiumSignedUp={patient?.premium_activation_state === "signed_up"}
          isStockholm={patient?.payer?.payer_type === PayerTypes.STOCKHOLM}
          isVGR={patient?.payer?.payer_type === PayerTypes.VGR}
        />
      )}
      {showUserCourseNotification && <UserCourseNotificationBox />}
      {showGuide()}
      {profile?.therapist_profile?.feature_flags?.includes("BOOKING_V2") && <UpcomingAppointments />}
      {hasFeature(Feature.PATIENT_SIGN_NOTES_TABLE) && <SignNotesTable />}
      {isPhysician && <PhysicianToDoTable patientId={patient?.id} />}
      {!patientError && <PatientMenu />}
    </Content>
  );
};

const Content = styled(VideoCallAwareContainer)`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  gap: 32px;
`;
